import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useMemo, useState} from 'react';

import {AnalyticsMetadataForContext} from 'analytics/AnalyticsTypes';
import {assert} from 'Asserts';
import {
  BenchlingExpression,
  BenchlingExpressionFunction,
  BenchlingExpressionType,
} from 'benchling-expressions/BenchlingExpressionTypes';
import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getClientIdForProcessFlowchartEdge} from 'processes/graph-builder-processes/flowchart-viz/util/ConvertProcessFlowchartForReactFlowUtil';
import {ProcessGraphBuilderNode} from 'processes/graph-builder-processes/validation/ProcessGraphBuilderConstants';
import {LogicalFunctionConfigErrors} from 'processes/modals/router-config/LogicalFunctionConfig';
import MultipleConditionConfig from 'processes/modals/router-config/MultipleConditionConfig';
import {DEFAULT_OPERATOR} from 'processes/modals/router-config/MultipleConditionConfig';
import {
  getPartialExpression,
  NODE_DETAILS_TYPE_TO_ICON,
  expressionFunctionIsBoolean,
} from 'processes/modals/router-config/RouterConfigUtils';
import {Schema} from 'schemas/SchemaConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {GRAY_DARKER, GRAY_LIGHTER, RED} from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import EditableTextField from 'ui-library/inputs/EditableTextField';
import {MenuHeight, MenuWidth} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import {DeepPartial} from 'util/Types';

import {ProcessOutputSchemaGraphQL} from '../../../../graphql-fragments/processes/schemas/__generated/ProcessOutputSchemaGraphQL';
import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import {
  CreateOrUpdateRouterFunctionSpec,
  CreateRouterFunctionSpec,
  sharedStyles,
} from './RouterConfigConstants';

export interface ConditionalLogicConfigProps {
  routerNodeClientId: string;
  flowchartClientId: string;
  onIsRouterFunctionNameBeingEdited: (isBeingEdited: boolean) => void;
  functionSpec: CreateOrUpdateRouterFunctionSpec & CreateRouterFunctionSpec;
  setFunctionSpec: (newFunctionSpec: CreateOrUpdateRouterFunctionSpec & CreateRouterFunctionSpec) => void;
  inputNode: ProcessGraphBuilderNode;
  flowchartTaskSchema: Schema;
  immediateChildNodes: Array<ProcessGraphBuilderNode>;
  inputSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL | ProcessOutputSchemaGraphQL;
  didInitialFlowchartHaveChainedRouters: boolean;
  doesCurrentFlowchartHaveChainedRouters: boolean;
  analyticsMetadata: AnalyticsMetadataForContext;
  dataTestAttrs?: DataTestAttrs;
  errors: LogicalFunctionConfigErrors;
}

const NEXT_NODE_PLACEHOLDER_TEXT = 'select destination';

export default function ConditionalLogicConfig(props: ConditionalLogicConfigProps): JSX.Element {
  const [toNodeName, setToNodeName] = useState('');
  const [toNodeType, setToNodeType] = useState<ProcessFlowchartNodeDetailsType>();
  const [toClientId, setToClientId] = useState(props.functionSpec.edgeConfigReference?.toNodeClientId);

  const expression = useMemo(
    () => getPartialExpression(props.functionSpec.expression as DeepPartial<BenchlingExpression>),
    [props.functionSpec.expression]
  );

  useMemo(() => {
    // Check specifically for null since undefined means that the route exists but has not yet been configured.
    // eslint-disable-next-line benchlint/no-eqeqeq-null
    if (props.functionSpec.edgeConfigReference === null) {
      // If the edge config reference is null, the destination node is the flowchart output.
      setToNodeName('Outputs');
      setToNodeType(undefined);
    } else if (toClientId == null) {
      setToNodeName(NEXT_NODE_PLACEHOLDER_TEXT);
      setToNodeType(undefined);
    } else {
      const initialToNode = props.immediateChildNodes.find((n) => n.clientId === toClientId);
      if (initialToNode == null) {
        // The node this route goes has been disconnected but the route has not yet been decommissioned
        setToClientId(undefined);
      } else {
        setToNodeName(initialToNode.name ?? NEXT_NODE_PLACEHOLDER_TEXT);
        setToNodeType(initialToNode.type);
      }
    }
  }, [props.immediateChildNodes, props.functionSpec.edgeConfigReference, toClientId]);

  function wrapExpression(
    expr: DeepPartial<BenchlingExpressionFunction> | null
  ): DeepPartial<BenchlingExpressionFunction> {
    return {
      type: BenchlingExpressionType.FUNCTION,
      name: DEFAULT_OPERATOR.functionName,
      parameters: [
        {
          name: DEFAULT_OPERATOR.parameters[0].spreadParameterName,
          expression: expr ?? undefined,
        },
      ],
    };
  }

  // Singular non-boolean conditions should be unwrapped so as to avoid changing
  // expressions that were created prior to and/or condition support.
  function setExpressionInFunctionSpec(expr: DeepPartial<BenchlingExpression>): void {
    let funcExpr = getPartialExpression(expr);
    if (expressionFunctionIsBoolean(funcExpr)) {
      assert(funcExpr != null, 'Expected expression to be defined');
      if (funcExpr.parameters != null && funcExpr.parameters.length === 1) {
        const firstExpression = getPartialExpression(funcExpr.parameters[0]!.expression);
        if (!expressionFunctionIsBoolean(firstExpression)) {
          funcExpr = firstExpression;
        }
      }
    }
    props.setFunctionSpec({...props.functionSpec, expression: funcExpr, isConfigured: false});
  }

  return (
    <div className={classNames(css(styles.routeBlock))} {...getTestProps(props.dataTestAttrs)}>
      <div className={css(styles.rowHeader)}>
        <div className={css(sharedStyles.routerOrRouteName)}>
          <EditableTextField
            value={props.functionSpec.name}
            onIsBeingEditedChanged={props.onIsRouterFunctionNameBeingEdited}
            requestChange={(newName) => {
              props.setFunctionSpec({...props.functionSpec, name: String(newName)});
            }}
            dataTestAttrs={{component: 'ConditionalLogicConfig', element: 'functionName'}}
          />
        </div>
      </div>
      {props.functionSpec.isDefault ? null : (
        <MultipleConditionConfig
          routerNodeClientId={props.routerNodeClientId}
          flowchartClientId={props.flowchartClientId}
          expression={
            expression != null && expressionFunctionIsBoolean(expression)
              ? expression
              : wrapExpression(expression)
          }
          setExpression={setExpressionInFunctionSpec}
          onIsRouterFunctionNameBeingEdited={props.onIsRouterFunctionNameBeingEdited}
          inputNode={props.inputNode}
          inputSchema={props.inputSchema}
          immediateChildNodes={props.immediateChildNodes}
          flowchartTaskSchema={props.flowchartTaskSchema}
          didInitialFlowchartHaveChainedRouters={props.didInitialFlowchartHaveChainedRouters}
          doesCurrentFlowchartHaveChainedRouters={props.doesCurrentFlowchartHaveChainedRouters}
          analyticsMetadata={props.analyticsMetadata}
          dataTestAttrs={{
            component: 'ConditionalLogicConfig',
            element: 'MultipleConditionConfig',
            key: '0.0',
          }}
          errors={props.errors}
          recursiveDepth={0}
        />
      )}
      <div className={css(sharedStyles.row)}>
        {props.functionSpec.isDefault ? 'Go to' : 'then go to'}
        <Tooltip text={toNodeName} placement={FloatingPlacement.TOP} isDisabled={toClientId == null}>
          <SelectDropdown
            className={css(styles.routerInputControl)}
            buttonClassName={classNames({'fieldInput-input--error': props.errors.isNextNodeDuplicate})}
            displayName={toNodeName}
            shouldEllipsize={true}
            icon={toNodeType ? NODE_DETAILS_TYPE_TO_ICON[toNodeType] : undefined}
            iconClassName={css(styles.iconColor)}
            menu={
              <SearchableMenu
                height={MenuHeight.MEDIUM}
                width={MenuWidth.FULL}
                scrollable={true}
                optionDatas={props.immediateChildNodes.map((node, index) => ({
                  name: node.name,
                  value: node,
                  dataTestAttrs: {
                    component: 'ConditionalLogicConfig',
                    element: 'nextNodeOptionData',
                    index: `${props.dataTestAttrs?.key}-${index}`,
                  },
                  icon: node.type ? NODE_DETAILS_TYPE_TO_ICON[node.type] : undefined,
                  iconClassName: css(styles.iconColor),
                }))}
                selectedValue={toNodeName}
                onSelect={(node: ProcessGraphBuilderNode) => {
                  setToClientId(node.clientId);
                  const baseEdgeReference = {
                    clientId: getClientIdForProcessFlowchartEdge(props.routerNodeClientId, node.clientId),
                    fromNodeClientId: props.routerNodeClientId,
                    toNodeClientId: node.clientId,
                  };

                  props.setFunctionSpec({
                    ...props.functionSpec,
                    edgeConfigReference: baseEdgeReference,
                  });
                }}
              />
            }
            dataTestAttrs={{component: 'ConditionalLogicConfig', element: 'toNodeDropdown'}}
          />
        </Tooltip>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  routeBlock: {
    backgroundColor: GRAY_LIGHTER,
    margin: 16,
    marginTop: 0,
    paddingBlock: 8,
    paddingInline: 16,
  },
  rowHeader: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  errorIconWrapper: {
    marginRight: 8,
  },
  errorIcon: {
    color: RED,
  },
  errorText: {
    color: RED,
  },
  iconColor: {
    color: GRAY_DARKER,
  },
  routerInputControl: {
    width: 278,
  },
});
